import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getApiUrl } from "../config/api";

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    isLoading: auth0Loading,
  } = useAuth0();
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchUserProfile = useCallback(async () => {
    if (!isAuthenticated || hasFetched) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      const apiUrl = getApiUrl("users/me");

      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        const data = await response.json();
        if (data.needsRegistration) {
          setUserProfile({ needsRegistration: true });
          setHasFetched(true);
          setIsLoading(false);
          return;
        }
      }

      if (!response.ok) {
        // If the API is not available, set a default state instead of infinite loading
        if (response.status >= 500 || response.status === 0) {
          setUserProfile({ needsRegistration: true });
          setHasFetched(true);
          setIsLoading(false);
          return;
        }
        throw new Error(
          `API Error: ${response.status} - Failed to fetch user profile`
        );
      }

      const profile = await response.json();
      setUserProfile(profile);
      setHasFetched(true);
    } catch (err) {
      console.error("fetchUserProfile error:", err);

      // If it's a network error or the API is not available, set a default state
      if (err.name === "TypeError" || err.message.includes("fetch")) {
        setUserProfile({ needsRegistration: true });
        setHasFetched(true);
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently, hasFetched]);

  // Fetch profile when authenticated
  useEffect(() => {
    if (isAuthenticated && !hasFetched) {
      fetchUserProfile();
    } else if (!isAuthenticated) {
      setUserProfile(null);
      setHasFetched(false);
      setIsLoading(false);
      setError(null);
    }
  }, [isAuthenticated, hasFetched, fetchUserProfile]);

  // Refresh profile function (forces a new fetch)
  const refreshProfile = useCallback(async () => {
    setHasFetched(false);
    setIsLoading(true);

    if (!isAuthenticated) return;

    try {
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await fetch(getApiUrl("users/me"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        const data = await response.json();
        if (data.needsRegistration) {
          setUserProfile({ needsRegistration: true });
          setHasFetched(true);
          setIsLoading(false);
          return;
        }
      }

      if (!response.ok) {
        throw new Error("Failed to fetch user profile");
      }

      const profile = await response.json();
      setUserProfile(profile);
      setHasFetched(true);
    } catch (err) {
      setError(err.message);
      console.error("Error refreshing user profile:", err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  const value = {
    userProfile,
    isLoading: auth0Loading || isLoading,
    error,
    refreshProfile,

    // Convenience methods for role checking
    isOrgAdmin: userProfile?.roles?.isOrgAdmin || false,
    isBusinessUnitHead: userProfile?.roles?.isBusinessUnitHead || false,
    isBusinessUser: userProfile?.roles?.isBusinessUser || false,
    hasAnyRole: userProfile?.hasAnyRole || false,
    canManageUsers: userProfile?.canManageUsers || false,
    canCreateResources: userProfile?.canCreateResources || false,
    primaryRole: userProfile?.primaryRole || "no_role",

    // Permission helpers
    canManageOrganization: (orgId) => {
      return (
        userProfile?.permissions?.organizations?.some(
          (org) => org.id === orgId && org.role === "admin"
        ) || false
      );
    },

    canManageBusinessUnit: (businessUnitId) => {
      return (
        userProfile?.permissions?.businessUnitHeadOf?.some(
          (bu) => bu.id === businessUnitId
        ) ||
        userProfile?.roles?.isOrgAdmin ||
        false
      );
    },

    isBusinessUnitMember: (businessUnitId) => {
      return (
        userProfile?.permissions?.businessUnitMemberOf?.some(
          (bu) => bu.id === businessUnitId
        ) || false
      );
    },
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
